import React, { useCallback, useMemo, useRef, useState } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import block from 'bem-cn'

import menuIcon from 'shared/img/navIcons/menu.png'
import { ReactComponent as MenuSVG } from 'shared/img/navIcons/menu.svg'

import { useExitAnimation } from 'shared/hooks/useExitAnimation'
import { useOutsideClick } from 'shared/hooks/useOutsideClick'
import { localeCommonKeysType } from 'shared/locale/en-US'

import { selectIsUserAuthenticated } from 'features/auth/selectors'
import { selectLocaleDict } from 'features/locale/selectors'

import { useModalContext } from 'components/HOC/withModalQuery/ModalContext'

import { addNotify } from 'components/Notify'
import { SideMenu } from 'components/SideMenu/SideMenu'

import { INavMenuItem, navMenuItems } from './data'

import './NavMenuMobile.scss'

const b = block('nav-menu-mobile')

const NavMenuMobile: React.FC = () => {
  const { pathname, search } = useLocation()
  const history = useHistory()

  const { pushNewModal } = useModalContext()
  const locale = useSelector(selectLocaleDict, shallowEqual).common
  const localeSlots = useSelector(selectLocaleDict, shallowEqual).slots
  const isAuth = useSelector(selectIsUserAuthenticated, shallowEqual)

  const [isSideMenuOpen, setIsSideMenuOpen] = useState<boolean>(false)

  const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null)
  const profileMenuRef = useRef(null)

  const handleToggleSubMenu = (index: number | null) => {
    setOpenMenuIndex(prev => (prev === index ? null : index))
  }

  const handleToggleSideMenu = useCallback(() => {
    setIsSideMenuOpen(prev => !prev)
  }, [])

  const handleLinkClick = useCallback(
    (item: { route: string; isAuthRequired?: boolean; isModal?: boolean }) => {
      if (item.isAuthRequired && !isAuth) {
        addNotify(localeSlots.pleaseAuth)
        return
      }

      if (item.isModal) {
        pushNewModal(item.route)()
        return
      }

      history.push(item.route)
      handleToggleSubMenu(null)
    },
    [isAuth, localeSlots, history, pushNewModal],
  )

  const isFakeMenuOpen = useExitAnimation({ disappearDuration: 350, realIsOpen: Boolean(openMenuIndex) })

  useOutsideClick(profileMenuRef, () => setOpenMenuIndex(null))

  const navItems = useMemo(
    () =>
      navMenuItems.map((item, index) => {
        const isActive = item.route === pathname
        const isSubMenuOpen = openMenuIndex === index

        return (
          <div key={item.title} className={b('item-wrapper')}>
            <div
              className={b('item', { isActive })}
              onClick={() => (item.subItems ? handleToggleSubMenu(index) : handleLinkClick(item))}>
              <img src={item.img} alt={item.title} className={b('item-icon')} />
              <span className={b('item-text')}>{locale[item.title as localeCommonKeysType] || item.title}</span>
            </div>
            {item.subItems && isSubMenuOpen && (
              <div className={b('sub-menu', { closed: !isSubMenuOpen })}>
                {item.subItems.map((subItem, subIndex) => {
                  const isSubItemActive = subItem.route === `${pathname}${search}`
                  return (
                    <div
                      key={subIndex}
                      className={b('sub-menu-item', { isActive: isSubItemActive })}
                      onClick={() => handleLinkClick(subItem)}>
                      {locale[subItem.name]}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )
      }),
    [pathname, openMenuIndex, locale, handleLinkClick, search],
  )

  return (
    <nav className={b()}>
      <SideMenu isOpen={isSideMenuOpen} onToggleSideMenu={handleToggleSideMenu} />
      <ul className={b('items')} ref={profileMenuRef}>
        <li className={b('item-wrapper')}>
          <div className={b('item')} onClick={handleToggleSideMenu}>
            <img src={menuIcon} alt={locale.menu} className={b('item-icon')} />
            <span className={b('item-text')}>{locale.menu}</span>
          </div>
        </li>
        {navItems}
      </ul>
    </nav>
  )
}

export default NavMenuMobile
